/* eslint-disable @typescript-eslint/naming-convention */
import {
  ButtonLooks,
  CheckboxSwitch,
  StandardButton,
  StandardPagination,
  StandardTable,
  StandardTableColumn,
  StandardTableRow
} from '@brandfolder/react';
import { Trans, t } from '@lingui/macro';
import { decode } from 'html-entities';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { TagCountResponse, TagSortByOptions } from '@components/bulk_management/tags/helpers';
import { Locale } from '@components/common/language_menu/languagesMap';
import { genericError } from '@components/standard-messages';
import { getAssetLibraryTitle } from '@helpers/getty-strings';

import { DeleteTag } from './DeleteTag';
import './styles/main.scss';
import { TagName } from './TagName';
import { TagsSortingOptions } from './TagsSortingOptions';

const PAGE_SIZE = 100;

export interface TagsTableProps {
  autoGenerated: boolean;
  error: boolean;
  loading: boolean;
  setAutoGenerated: SetStateDispatch<boolean>;
  setTags: SetStateDispatch<TagCountResponse>;
  setUgtLocale: SetStateDispatch<Locale>;
  tags: TagCountResponse;
  ugtLocale: Locale;
}

export const TagsTable: FunctionComponent<TagsTableProps> = ({
  autoGenerated,
  error,
  loading,
  setAutoGenerated,
  setTags,
  setUgtLocale,
  tags,
  ugtLocale,
}) => {
  const [sortedTagNames, setSortedTagNames] = useState<string[] | null>(null);
  /**
   * queryString state lives here and not in TagsSortingOptions
   * because the empty state needs to know whether the Brandfolder is empty
   * or if the query doesn't match anything
   */
  const [queryString, setQueryString] = useState('');
  const [sortBy, setSortBy] = useState<TagSortByOptions>('name');
  const [resetSearch, setResetSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const resetSearchQuery = (): void => {
    setQueryString('');
    // pass in resetSearch to TagsSortingOptions
    // so it only focuses when explicit reset is requested
    setResetSearch(true);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [queryString, sortBy]);

  const columns: StandardTableColumn[] = [{
    children: <Trans>Tags</Trans>,
    rowKey: 'tagName',
  }, {
    children: <Trans># Assets</Trans>,
    rowKey: 'assetCount'
  }, {
    rowKey: 'delete'
  }];

  const noMatchingQuery = (
    <span className="manage-tags__no-query-results">
      <Trans>
        Sorry! No results were found.
        <StandardButton
          look={ButtonLooks.TextSecondary}
          onClick={resetSearchQuery}
        >
          Try a different search
        </StandardButton>
      </Trans>
    </span>
  );

  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;
  const pageEndIndex = pageStartIndex + PAGE_SIZE;
  const rows: StandardTableRow[] = (loading || !tags)
    ? []
    : sortedTagNames?.slice(pageStartIndex, pageEndIndex).map((tagName) => ({
      tagName: (
        <TagName
          autoGenerated={autoGenerated}
          setTags={setTags}
          tagName={tagName}
          tags={tags}
          ugtLocale={ugtLocale}
        />
      ),
      assetCount: <span className="manage-tags__asset-count">{tags[tagName]}</span>,
      delete: (
        <DeleteTag
          autoGenerated={autoGenerated}
          setTags={setTags}
          tagName={tagName}
          tags={tags}
          ugtLocale={ugtLocale}
        />
      )
    })) || [];

  return (
    <div className="manage-tags">
      <h2>
        {BFG.resource?.name
          ? <Trans>Tags for {decode(BFG.resource.name)}</Trans>
          : <Trans>Tags</Trans>}
      </h2>
      <hr />
      {!loading && (
        <TagsSortingOptions
          queryString={queryString}
          resetSearch={resetSearch}
          setQueryString={setQueryString}
          setResetSearch={setResetSearch}
          setSortBy={setSortBy}
          setSortedTagNames={setSortedTagNames}
          setUgtLocale={setUgtLocale}
          sortBy={sortBy}
          tags={tags}
          ugtLocale={ugtLocale}
        />
      )}
      {!(BFG.hasFeature('disable_google_vision_autotagging') || !BFG.brandfolderSettings?.auto_tagging) && (
        <div className="filter-by-auto-generated-tags">
          <CheckboxSwitch
            defaultChecked={autoGenerated}
            id="show-auto-generated-tags"
            label={<Trans>Show auto-generated tags</Trans>}
            onChange={(): void => setAutoGenerated(!autoGenerated)}
          />
        </div>
      )}
      <div className="manage-tags__table-container">
        <StandardTable
          caption={t`List of tags and number of associated assets`}
          columns={columns}
          empty={!loading && !error && rows.length === 0}
          emptyContent={queryString ? noMatchingQuery : <Trans>This {getAssetLibraryTitle()} has no tags.</Trans>}
          error={error && !loading}
          errorContent={genericError()}
          id="bulk-tags-table"
          loaderLabel={t`Loading tags`}
          loading={loading}
          rows={rows}
          scrollY={false}
        />
        {sortedTagNames && sortedTagNames.length > PAGE_SIZE && (
          <StandardPagination
            initial={currentPage}
            labels={{
              nextLabel: t`Next page`,
              previousLabel: t`Previous page`
            }}
            onPageChange={(page: number): void  => {
              setCurrentPage(page);
            }}
            total={Math.ceil(sortedTagNames.length / PAGE_SIZE)}
          />
        )}
      </div>
    </div>
  );
};
